import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Divider, Paper, Tooltip, Typography } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { ProjectType_Entity } from "../../entities/projectType";
import { Scale_Entity } from "../../entities/scale";
import { TaskType_Entity } from "../../entities/taskType";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import {
  useCreateScaleMutation,
  useDeleteScaleMutation,
  useUpdateScaleMutation,
} from "../../redux/scale/scaleEndpoints";
import { useScales } from "../../redux/scale/scaleHooks";
import { useTaskTypes } from "../../redux/taskType/taskTypeHooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { setFormOpen, setSelectedScaleID } from "../../redux/filmsReducer";

/**
 * Scale
 *
 * @returns {null} Scale
 */
export default function Scales() {
  const { scales } = useScales();
  const { projectTypes } = useProjectTypes();
  const { taskTypes } = useTaskTypes();
  const [createScale] = useCreateScaleMutation();
  const [deleteScale] = useDeleteScaleMutation();
  const [updateScale] = useUpdateScaleMutation();

  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  const onScaleUpdate = async (
    _newScale: Scale_Entity,
    _oldScale: Scale_Entity
  ) => {
    // Wait for the server rather than resolving on a 50ms timer: the grid must
    // not leave edit mode before the write lands, or the refetch that follows
    // reverts the cell to its pre-edit value.
    await updateScale({ id: _newScale.id, body: _newScale }).unwrap();
    return { ..._newScale };
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        apiRef={apiRef}
        rows={scales ?? []}
        columns={scaleColumns(
          dispatch,
          confirm,
          projectTypes ?? [],
          taskTypes ?? [],
          deleteScale
        )}
        onRowClick={(e) => {
          dispatch(setSelectedScaleID(e.row.id));
          dispatch(setFormOpen({ isOpen: true, formID: "scale" }));
        }}
        showToolbar
        slots={{
          toolbar: ScaleToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        initialState={{
          pinnedColumns: {
            left: ["id"],
            right: ["actions"],
          },
        }}
      />
    </Paper>
  );
}

function ScaleToolbar() {
  const [createScale] = useCreateScaleMutation();
  const handleNewScale = () => {
    const newScale = {
      id: -1,
      projectTypeID: 0,
      taskTypeID: 0,
      rate: 0,
      createdAt: "",
      updatedAt: "",
    };
    createScale(newScale);
  };

  useHotkeys(`ctrl+enter`, handleNewScale, {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <Toolbar>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        <Tooltip title="ctrl + enter">
          <Button variant="soft" size="sm" onClick={handleNewScale}>
            + New Scale
          </Button>
        </Tooltip>
        <Divider
          sx={{ margin: 1, width: 2 }}
          orientation="vertical"
          variant="middle"
          flexItem
        />
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

const scaleColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  projectTypes: ProjectType_Entity[],
  taskTypes: TaskType_Entity[],
  deleteScale: (id: number) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "number",
      width: 40,
    },
    {
      field: "projectTypeID",
      headerName: "Project Type",
      editable: true,
      width: 200,
      type: "singleSelect",
      valueOptions: getOptions(projectTypes),
      renderCell: (p: any) => {
        const projectType = projectTypes.find(
          (s) => s.id === p.row.projectTypeID
        );
        return <Typography variant="body2">{projectType?.name}</Typography>;
      },
    },
    {
      field: "taskTypeID",
      headerName: "Task Type",
      editable: true,
      width: 200,
      type: "singleSelect",
      valueOptions: getOptions(taskTypes),
      renderCell: (p: any) => {
        const taskType = taskTypes.find((s) => s.id === p.row.taskTypeID);
        return <Typography variant="body2">{taskType?.name}</Typography>;
      },
    },
    {
      field: "rate",
      headerName: "Rate",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("rate", 10000),
      valueSetter: setterDivider("rate", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any }) => [
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label={`Delete`}
          onClick={() => {
            confirm({
              description: `You are about to delete a scale`,
            })
              .then(() => {
                deleteScale(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);

function getOptions(items: { id: number; name?: string }[]) {
  const res = [];
  for (const key in items) {
    if (Object.prototype.hasOwnProperty.call(items, key)) {
      const item = items[key];
      res.push({
        value: item.id,
        label: `${item.id}: ${item.name}`,
      });
    }
  }

  return res;
}
